import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { IconPlus, IconSearch, IconLayoutGrid, IconList, IconFilter, IconBox, IconCheck } from '@tabler/icons-react'
import { GearCard, GearHoverCard, type GearCardItem } from './GearCard.tsx'
import { GearItemForm } from './GearItemForm.tsx'
import { gearItemsApi, kitCollectionsApi, type GearItem } from '../../lib/api.ts'
import { useToast } from '../../components/ui/Toast.tsx'
import { InlineError } from '../../components/ui/ErrorBoundary.tsx'

type ViewMode = 'grid' | 'list'
type FormMode = 'new' | 'edit' | 'view'

function toCardItem(item: GearItem): GearCardItem {
  return {
    id: item.id,
    title: item.title,
    brand: item.brand ?? undefined,
    size: (item as any).sizeDisplayLabel ?? item.size ?? undefined,
    isFavorite: item.isFavorite ?? false,
    condition: item.attributes.find(a => a.attributeType === 'condition')?.value?.displayLabel,
    visibility: item.visibility ?? 'private',
    photos: item.photos.map(p => ({ url: p.url ?? p.storageKey, isPrimary: p.isPrimary })),
    colors: item.attributes.filter(a => a.attributeType === 'color').map(a => a.value?.displayLabel ?? '').filter(Boolean),
    activities: item.attributes.filter(a => a.attributeType === 'activity').map(a => a.value?.displayLabel ?? '').filter(Boolean),
  }
}

export function GearPage({ favoritesOnly = false }: { favoritesOnly?: boolean }) {
  const { error, success } = useToast()
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [search, setSearch] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()

  // Modal state
  const [formMode, setFormMode] = useState<FormMode>('new')
  const [formOpen, setFormOpen] = useState(() => searchParams.get('action') === 'new')
  const [selectedItem, setSelectedItem] = useState<GearItem | null>(null)

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<GearItem | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Add to bag state
  const [bagTarget, setBagTarget] = useState<GearItem | null>(null)
  const [bags, setBags] = useState<{ id: string; name: string }[]>([])
  const [bagSelections, setBagSelections] = useState<Set<string>>(new Set())
  const [savingBags, setSavingBags] = useState(false)

  const [items, setItems] = useState<GearItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Hover card state
  const [hoverItem, setHoverItem] = useState<GearCardItem | null>(null)
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleCardMouseEnter = (item: GearCardItem, e: React.MouseEvent) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    hoverTimer.current = setTimeout(() => {
      setHoverItem(item)
      setHoverRect(rect)
    }, 400)
  }

  const handleCardMouseLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    setHoverItem(null)
    setHoverRect(null)
  }

  const dismissHover = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    setHoverItem(null)
    setHoverRect(null)
  }

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const res = await gearItemsApi.list()
      setItems(res.data ?? [])
    } catch (e: any) {
      setLoadError(e.message ?? 'Failed to load gear items')
      error('Failed to load gear', e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const openNew = () => { dismissHover(); setSelectedItem(null); setFormMode('new'); setFormOpen(true) }
  const openView = (item: GearItem) => { dismissHover(); setSelectedItem(item); setFormMode('view'); setFormOpen(true) }
  const openEdit = (item: GearItem) => { dismissHover(); setSelectedItem(item); setFormMode('edit'); setFormOpen(true) }

  const handleClose = () => {
    setFormOpen(false)
    setSelectedItem(null)
    if (searchParams.get('action') === 'new') {
      searchParams.delete('action')
      setSearchParams(searchParams)
    }
  }

  const handleSaved = () => { load() }

  const openAddToBag = async (item: GearItem) => {
    dismissHover()
    setBagTarget(item)
    setBagSelections(new Set())
    try {
      const res = await kitCollectionsApi.list()
      setBags((res.data ?? []).map((b: any) => ({ id: b.id, name: b.name })))
    } catch { setBags([]) }
  }

  const saveBagAssignments = async () => {
    if (!bagTarget) return
    setSavingBags(true)
    try {
      await Promise.all([...bagSelections].map(bagId => kitCollectionsApi.addItem(bagId, bagTarget.id)))
      success('Added to bag', `${bagTarget.title} added to ${bagSelections.size} bag${bagSelections.size !== 1 ? 's' : ''}`)
      setBagTarget(null)
    } catch (e: any) {
      error('Failed to add to bag', e.message)
    } finally { setSavingBags(false) }
  }

  const handleToggleFavorite = async (item: GearItem) => {
    try {
      await gearItemsApi.toggleFavorite(item.id)
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, isFavorite: !i.isFavorite } : i))
    } catch (e: any) {
      error('Failed to update favorite', e.message)
    }
  }

  const handleDuplicate = async (item: GearItem) => {
    try {
      const payload = {
        title: `${item.title} (Copy)`,
        brand: item.brand,
        description: item.description,
        notes: item.notes,
        conditionId: item.conditionId,
        statusId: item.statusId,
        visibilityId: item.visibilityId,
        exchangeAvailabilityId: item.exchangeAvailabilityId,
        containerId: item.containerId,
        sizeCategoryId: item.sizeCategoryId,
        size: item.size,
        acquisitionSource: item.acquisitionSource,
        acquisitionPrice: item.acquisitionPrice,
        acquisitionDate: item.acquisitionDate,
        tradeSellValue: item.tradeSellValue,
        attributes: item.attributes.map(a => ({
          attributeType: a.attributeType,
          valueId: a.valueId,
          priority: a.priority ?? 0,
        })),
        photos: [],
      }
      const res = await gearItemsApi.create(payload)
      await load()
      // Open the new item in edit mode
      const newItem = res.data
      if (newItem) {
        setSelectedItem(newItem)
        setFormMode('edit')
        setFormOpen(true)
      }
      success('Duplicated', `${item.title} (Copy) created`)
    } catch (e: any) {
      error('Failed to duplicate', e.message)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await gearItemsApi.delete(deleteTarget.id)
      success('Item deleted', deleteTarget.title)
      setDeleteTarget(null)
      load()
    } catch (e: any) {
      error('Failed to delete', e.message)
    } finally {
      setDeleting(false) }
  }

  const cardItems = items.map(toCardItem)
  // Keep reference to full GearItem by id for opening view
  const itemById = Object.fromEntries(items.map(i => [i.id, i]))

  const filtered = cardItems.filter(i => {
    if (favoritesOnly && !i.isFavorite) return false
    return (
      i.title.toLowerCase().includes(search.toLowerCase()) ||
      (i.brand ?? '').toLowerCase().includes(search.toLowerCase())
    )
  })

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border-default flex-wrap">
        <h1 className="text-sm font-medium text-zinc-200 flex-1">
          {favoritesOnly ? 'Favorites' : 'My Gear'} <span className="ml-2 text-zinc-500 font-normal text-xs">· {filtered.length} items</span>
        </h1>
        <div className="relative">
          <IconSearch size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search gear..."
            className="pl-7 pr-3 py-1.5 bg-surface-raised border border-border-default rounded-lg text-xs text-zinc-300 placeholder-zinc-500 outline-none focus:border-brand-orange/50 w-44" />
        </div>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-border-default text-zinc-400 hover:text-zinc-200 hover:bg-surface-raised transition-colors">
          <IconFilter size={13} /> Filter
        </button>
        <div className="flex items-center bg-surface-raised border border-border-default rounded-lg p-0.5 gap-0.5">
          <button onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-surface-float text-zinc-200' : 'text-zinc-500 hover:text-zinc-300'}`}>
            <IconLayoutGrid size={14} />
          </button>
          <button onClick={() => setViewMode('list')}
            className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-surface-float text-zinc-200' : 'text-zinc-500 hover:text-zinc-300'}`}>
            <IconList size={14} />
          </button>
        </div>
        <button onClick={openNew}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium bg-brand-orange text-white hover:bg-brand-orange-dark transition-colors">
          <IconPlus size={14} /> Add gear
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loadError ? (
          <div className="p-6">
            <InlineError message={loadError} onRetry={load} />
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm text-zinc-500">Loading gear...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-surface-raised border border-border-default flex items-center justify-center mx-auto mb-4">
                <IconBox size={24} className="text-zinc-600" />
              </div>
              <h3 className="text-sm font-medium text-zinc-400 mb-1">
                {search ? 'No matching gear' : 'No gear yet'}
              </h3>
              <p className="text-xs text-zinc-500 mb-4 max-w-xs">
                {search ? 'Try a different search term.' : 'Start building your locker by adding your first piece of gear.'}
              </p>
              {!search && (
                <button onClick={openNew}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-orange text-white text-xs font-medium hover:bg-brand-orange-dark transition-colors mx-auto">
                  <IconPlus size={14} /> Add your first piece of gear
                </button>
              )}
            </div>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filtered.map(item => (
              <div key={item.id}
                onMouseEnter={e => handleCardMouseEnter(item, e)}
                onMouseLeave={handleCardMouseLeave}>
                <GearCard item={item}
                  onView={() => openView(itemById[item.id])}
                  onEdit={() => openEdit(itemById[item.id])}
                  onDelete={() => { dismissHover(); setDeleteTarget(itemById[item.id]) }}
                  onToggleFavorite={() => handleToggleFavorite(itemById[item.id])}
                  onDuplicate={() => handleDuplicate(itemById[item.id])}
                  onAddToBag={() => openAddToBag(itemById[item.id])} />
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 space-y-2 max-w-3xl">
            {filtered.map(item => {
              const fullItem = itemById[item.id]
              const secondaryParts = [item.brand, item.size].filter(Boolean)
              return (
                <div key={item.id}
                  onClick={() => openView(fullItem)}
                  onContextMenu={(e) => { e.preventDefault(); openEdit(fullItem) }}
                  onMouseEnter={e => handleCardMouseEnter(item, e)}
                  onMouseLeave={handleCardMouseLeave}
                  className="flex items-center gap-4 p-3 rounded-xl bg-surface-overlay border border-border-default hover:border-border-strong transition-colors cursor-pointer">
                  <div className="w-12 h-16 rounded-lg overflow-hidden bg-surface-raised shrink-0">
                    {item.photos[0] ? (
                      <img src={item.photos[0].url} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <IconBox size={16} className="text-zinc-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-200 line-clamp-2">{item.title}</div>
                    {secondaryParts.length > 0 && (
                      <div className="text-xs text-zinc-500 mt-0.5">{secondaryParts.join(' · ')}</div>
                    )}
                  </div>
                  {item.condition && (
                    <div className="text-xs text-zinc-500 shrink-0">{item.condition}</div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Hover card */}
      <GearHoverCard item={hoverItem!} visible={!!hoverItem} anchorRect={hoverRect} />

      <GearItemForm
        mode={formMode}
        item={selectedItem ?? undefined}
        open={formOpen}
        onClose={handleClose}
        onSaved={handleSaved}
        onModeChange={setFormMode}
      />

      {/* Add to Bag picker */}
      {bagTarget && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-surface-overlay border border-border-default rounded-2xl shadow-2xl p-6">
            <h3 className="text-sm font-semibold text-zinc-100 mb-1">Add to Bag</h3>
            <p className="text-xs text-zinc-400 mb-4 truncate">{bagTarget.title}</p>
            {bags.length === 0 ? (
              <p className="text-xs text-zinc-500 mb-4">No gear bags yet. Create a bag first.</p>
            ) : (
              <div className="space-y-1 mb-4 max-h-60 overflow-y-auto">
                {bags.map(bag => {
                  const checked = bagSelections.has(bag.id)
                  return (
                    <button key={bag.id} type="button"
                      onClick={() => setBagSelections(prev => {
                        const next = new Set(prev)
                        checked ? next.delete(bag.id) : next.add(bag.id)
                        return next
                      })}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors text-left
                        border-border-default hover:border-border-strong hover:bg-surface-raised">
                      <div className={[
                        'w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors',
                        checked ? 'bg-brand-orange border-brand-orange' : 'border-zinc-600',
                      ].join(' ')}>
                        {checked && <IconCheck size={10} className="text-white" strokeWidth={3} />}
                      </div>
                      <span className="text-sm text-zinc-200 truncate">{bag.name}</span>
                    </button>
                  )
                })}
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setBagTarget(null)}
                className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:text-zinc-200 hover:bg-surface-raised transition-colors">
                Cancel
              </button>
              <button onClick={saveBagAssignments}
                disabled={savingBags || bagSelections.size === 0}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {savingBags ? 'Adding...' : `Add to ${bagSelections.size || ''} Bag${bagSelections.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-surface-overlay border border-border-default rounded-2xl shadow-2xl p-6">
            <h3 className="text-sm font-semibold text-zinc-100 mb-2">Delete gear item?</h3>
            <p className="text-xs text-zinc-400 mb-5">
              <span className="font-medium text-zinc-200">{deleteTarget.title}</span> will be moved to Recently Deleted. You can restore it from there within 30 days.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:text-zinc-200 hover:bg-surface-raised transition-colors">
                Cancel
              </button>
              <button onClick={handleDeleteConfirm} disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors">
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
